// Google truncates the snippet near 160 chars and Facebook shows ~122, but Telegram and Slack
// render far more. 200 keeps the long-form consumers fed without tripling head weight.
const META_DESCRIPTION_MAX_LENGTH = 200

// Six entries cover the field: descriptions come from the PrimeVue/Quill editor
// (RichDescriptionInput.vue), which stores smart quotes and dashes as literal UTF-8 and
// normalises pasted html through its Delta pipeline, so `&rsquo;`/`&hellip;` never reach it.
const HTML_ENTITIES: Record<string, string> = {
  amp: '&',
  apos: "'",
  gt: '>',
  lt: '<',
  nbsp: ' ',
  quot: '"',
}

function codePointToText(codePoint: number, match: string): string {
  // fromCodePoint throws outside the Unicode range and lone surrogates aren't valid text.
  if (!Number.isFinite(codePoint) || codePoint < 1 || codePoint > 0x10ffff) return match
  if (codePoint >= 0xd800 && codePoint <= 0xdfff) return match
  return String.fromCodePoint(codePoint)
}

function decodeHtmlEntities(text: string): string {
  // One pass, so `&amp;lt;` decodes to the literal `&lt;` rather than to `<`.
  return text.replace(/&(#[xX][0-9a-fA-F]+|#\d+|[a-zA-Z]+);/g, (match, entity: string) => {
    if (entity.startsWith('#x') || entity.startsWith('#X')) {
      return codePointToText(parseInt(entity.slice(2), 16), match)
    }
    if (entity.startsWith('#')) return codePointToText(parseInt(entity.slice(1), 10), match)
    return HTML_ENTITIES[entity.toLowerCase()] ?? match
  })
}

// Only the end of a block becomes a space. Spacing every tag would put one in front of the
// punctuation that follows an inline tag, so `<strong>jazz</strong>,` reads as `jazz ,`.
// The cell close is what carries the break in a table; `</tr>` alone leaves `MonTue`.
const BLOCK_BOUNDARY = /<\/(?:p|div|li|h[1-6]|blockquote|tr|td|th)\s*>|<br\s*\/?>/gi

// Stripped whole, and before everything else: a comment can hold markup, and the generic tag
// strip below stops at the first `>`, so `<!-- a > b -->` would leak ` b -->` as prose.
const HTML_COMMENT = /<!--[\s\S]*?(?:-->|$)/g

// Dropped with their text, unlike every other tag: a stylesheet or a script body rendered as
// prose in a search snippet is worse than the markup this function is here to remove. An
// unterminated open tag runs to the end of the input rather than not matching at all — a truncated
// `<style>` is exactly the malformed input this rule exists for.
const SCRIPT_OR_STYLE = /<(script|style)\b[^>]*>[\s\S]*?(?:<\/\1\s*>|$)/gi

function truncateAtWord(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  const clipped = text.slice(0, maxLength - 1)
  const lastSpace = clipped.lastIndexOf(' ')
  // Slicing counts utf-16 units, so a cut through an astral character strands its high
  // surrogate. Drop that before the trailing punctuation, not after: the surrogate would
  // otherwise shield the punctuation from its own strip and leave `word.…`.
  const head = (lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped)
    .replace(/[\uD800-\uDBFF]$/, '')
    .replace(/[\s.,;:!?-]+$/, '')
  return head ? `${head}…` : ''
}

// Turns stored rich-text description html into the one bounded line that backs
// <meta name="description">, og:description and twitter:description.
export function buildMetaDescription(html: string | null | undefined): string {
  if (!html) return ''
  // Strip before decoding so `5 &lt; 6` survives — decoding first would let the tag regex eat
  // `< 6 >`. Decoded markup is harmless because this only ever lands in a meta `content`
  // attribute, which Vue escapes on both the SSR and client paths, never in v-html.
  const stripped = html
    .replace(HTML_COMMENT, ' ')
    .replace(SCRIPT_OR_STYLE, ' ')
    .replace(BLOCK_BOUNDARY, ' ')
  const text = decodeHtmlEntities(stripped.replace(/<[^>]+>/g, ''))
  return truncateAtWord(text.replace(/\s+/g, ' ').trim(), META_DESCRIPTION_MAX_LENGTH)
}
